import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import type { SongMusicVideoSummary } from 'fym-shared/src/music24/musicVideoApi';
import { useGetCatalogSongQuery, useGetReleaseKitQuery } from '../../../../../../redux/api/fymAPI';
import type { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { videoSizeLabel } from '../../../../../music24/promote/addedAssets/addedAssetFacts';
import { youtubeViewsOf } from '../../../../../music24/promote/addedAssets/youtubeViews';
import { addAssetRequestOf } from '../../../../../music24/promote/addedAssets/addAssetRequestOf';
import { openAddAssetDialog, type AddAssetTab } from '../../../../../music24/promote/addedAssets/addAssetDialogStore';
import { useOpenMusicVideoEditor } from '../../../../../music24/promote/addedAssets/useOpenMusicVideoEditor';
import { downloadMediaUrl } from '../../../../../music24/promote/inlineMediaUrl';
import { musicVideoFieldMessages as messages } from './musicVideoFieldMessages';
import { musicVideoFieldState } from './musicVideoFieldState';

/**
 * The data and the actions of the music video field (design music-video g): `song.musicVideo` of the
 * song answer (else of the catalog row), the kit asset of it (its download link and the cut that
 * made it), the "Add …" dialog and the editor.
 */
export function useSongMusicVideo(input: { accountId: number; item: CatalogItem }) {
  const { accountId, item } = input;
  const intl = useIntl();
  const { data: song } = useGetCatalogSongQuery({ accountId, itemUuid: item.uuid });
  const summary: SongMusicVideoSummary | null = song?.song.musicVideo ?? item.musicVideo ?? null;
  const state = musicVideoFieldState(summary);
  const { data: kit } = useGetReleaseKitQuery(
    { accountId, catalogItemUuid: item.uuid },
    { skip: summary === null || summary.source === 'youtube' }
  );
  const asset = useMemo(
    () =>
      summary === null
        ? null
        : ([...(kit?.assets ?? []), ...(kit?.newest ?? [])].find((one) => one.uuid === summary.assetUuid) ?? null),
    [kit, summary]
  );

  const badge = useMemo(() => {
    if (summary === null) {
      return null;
    }
    if (summary.youtube !== null) {
      // design music-video g2: "LaziiBee · 48,210 views" when we know the views.
      const views = youtubeViewsOf(summary.youtube);
      return views === null
        ? summary.youtube.channelTitle
        : intl.formatMessage(messages.badgeYouTubeViews, { channel: summary.youtube.channelTitle, count: views });
    }
    if (summary.source === 'upload') {
      const size = videoSizeLabel(asset?.height ?? null);
      return size === null
        ? intl.formatMessage(messages.badgeFile)
        : intl.formatMessage(messages.badgeFileSize, { size });
    }
    return summary.cutNumber === null ? null : intl.formatMessage(messages.badgeCut, { number: summary.cutNumber });
  }, [asset?.height, intl, summary]);

  const openAdd = useCallback(
    (tab?: AddAssetTab) => openAddAssetDialog(addAssetRequestOf(accountId, item, 'musicVideo', tab)),
    [accountId, item]
  );
  const openEditorOfSong = useOpenMusicVideoEditor(accountId);
  const openEditor = useCallback(
    () => openEditorOfSong(item.uuid, asset?.params.musicVideoCutUuid ?? null),
    [asset?.params.musicVideoCutUuid, item.uuid, openEditorOfSong]
  );

  return {
    summary,
    state,
    badge,
    downloadUrl: asset?.fileUrl === null || asset?.fileUrl === undefined ? null : downloadMediaUrl(asset.fileUrl),
    openAdd,
    openEditor,
  };
}
